'use client';

import { useState } from 'react';
import posthog from 'posthog-js';
import { useTranslations } from 'next-intl';
import {
  Clock,
  Handshake,
  Lock,
  Mail,
  MessageCircle,
  MessagesSquare,
  Send,
  Sparkles,
  Zap,
} from 'lucide-react';
import { BrandSparkle } from '@/components/brand/ornaments';
import { Header } from '@/components/layout/header';
import { Footer } from '@/components/layout/footer';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { PageHero } from '@/components/sections/page-hero';
import { TypographyH3, TypographyMuted, TypographySmall } from '@/components/ui/typography';
import { submitContactForm } from '@/lib/contact-form';

const TRUST_ICONS = [Zap, Sparkles, Handshake] as const;

export default function ContactPage() {
  const t = useTranslations('contact');
  const [status, setStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const [startedAt] = useState(() => Date.now().toString());

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setStatus('sending');

    const form = e.currentTarget;
    const data = new FormData(form);

    const result = await submitContactForm(data, fetch, (event) => posthog.capture(event));
    setStatus(result);
    if (result === 'success') form.reset();
  }

  return (
    <div className="flex min-h-screen flex-col">
      <Header />
      <main className="flex-1">
        <PageHero overline={t('overline')} heading={t('heading')} subheading={t('subheading')}>
          {/* Trust row — sits on the skyline band, above the form panel. */}
          <ul className="flex flex-wrap items-center justify-center gap-x-10 gap-y-5">
            {TRUST_ICONS.map((Icon, i) => (
              <li key={i} className="flex items-center gap-3 text-start">
                <span className="grid size-10 shrink-0 place-items-center rounded-md bg-[color-mix(in_srgb,var(--primary)_10%,transparent)] text-primary">
                  <Icon className="size-5" />
                </span>
                <span className="flex flex-col">
                  <TypographySmall className="font-semibold text-foreground">
                    {t(`trust.${i}.title`)}
                  </TypographySmall>
                  <TypographyMuted className="text-caption">
                    {t(`trust.${i}.subtitle`)}
                  </TypographyMuted>
                </span>
              </li>
            ))}
          </ul>
        </PageHero>

        <section className="relative z-10 pb-24 sm:pb-32 lg:pb-40">
          <div className="container-brand">
            <Card className="-mt-8 gap-0 overflow-hidden rounded-xl border-border/70 p-0 shadow-hero sm:-mt-14">
              <div className="grid lg:grid-cols-[minmax(0,0.82fr)_minmax(0,1.18fr)]">
                {/* Left — reassurance + ways to reach us. */}
                <div className="border-b p-8 sm:p-10 lg:border-b-0 lg:border-e">
                  <div className="relative inline-grid size-12 place-items-center rounded-full bg-primary text-primary-foreground">
                    <MessagesSquare className="size-5" />
                    <BrandSparkle className="absolute -end-1 -top-1 size-3.5 text-brand-gold" />
                  </div>

                  <TypographyH3 className="mt-6 font-display font-medium text-primary">
                    {t('panelTitle')}
                  </TypographyH3>
                  <TypographyMuted className="mt-3 max-w-xs">
                    {t('panelDescription')}
                  </TypographyMuted>

                  <div className="my-8 border-t border-border" />

                  <ul className="space-y-5">
                    <ContactMethod
                      icon={<Mail className="size-4" />}
                      label={t('emailLabel')}
                      value={t('emailValue')}
                      href={`mailto:${t('emailValue')}`}
                    />
                    <ContactMethod
                      icon={<MessageCircle className="size-4" />}
                      label={t('whatsappLabel')}
                      value={t('whatsappValue')}
                      href={`https://wa.me/${t('whatsappValue').replace(/[^\d]/g, '')}`}
                    />
                    <ContactMethod
                      icon={<Clock className="size-4" />}
                      label={t('hoursLabel')}
                      value={t('hoursValue')}
                    />
                  </ul>
                </div>

                {/* Right — the form. */}
                <div className="p-8 sm:p-10">
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <input type="hidden" name="_subject" value={t('emailSubject')} />
                    <input type="hidden" name="startedAt" value={startedAt} />
                    <input
                      type="text"
                      name="website"
                      autoComplete="off"
                      tabIndex={-1}
                      className="hidden"
                    />

                    <div className="grid gap-6 sm:grid-cols-2">
                      <div className="space-y-2">
                        <Label htmlFor="name">{t('name')}</Label>
                        <Input
                          id="name"
                          name="name"
                          type="text"
                          required
                          className="h-11"
                          placeholder={t('namePlaceholder')}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="email">{t('email')}</Label>
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          required
                          className="h-11"
                          placeholder={t('emailPlaceholder')}
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="company">{t('company')}</Label>
                      <Input
                        id="company"
                        name="company"
                        type="text"
                        className="h-11"
                        placeholder={t('companyPlaceholder')}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="message">{t('message')}</Label>
                      <Textarea
                        id="message"
                        name="message"
                        rows={5}
                        required
                        placeholder={t('messagePlaceholder')}
                      />
                    </div>

                    <Button
                      type="submit"
                      variant="default"
                      size="lg"
                      className="w-full"
                      disabled={status === 'sending'}
                      aria-busy={status === 'sending'}
                    >
                      {status === 'sending' ? (
                        t('sending')
                      ) : (
                        <>
                          <Send className="size-4 rtl:-scale-x-100" />
                          {t('submit')}
                        </>
                      )}
                    </Button>

                    <div className="flex items-center justify-center gap-2 text-center">
                      <Lock className="size-3.5 text-muted-foreground" aria-hidden />
                      <TypographyMuted className="text-caption">{t('secure')}</TypographyMuted>
                    </div>

                    {status === 'success' && (
                      <Alert>
                        <AlertDescription>{t('success')}</AlertDescription>
                      </Alert>
                    )}
                    {status === 'error' && (
                      <Alert variant="destructive">
                        <AlertDescription>{t('error')}</AlertDescription>
                      </Alert>
                    )}
                  </form>
                </div>
              </div>
            </Card>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}

/** A single way to reach us — outlined icon badge, label, and value. */
function ContactMethod({
  icon,
  label,
  value,
  href,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  href?: string;
}) {
  return (
    <li className="flex items-center gap-4">
      <span className="grid size-10 shrink-0 place-items-center rounded-full border border-border text-primary">
        {icon}
      </span>
      <span className="flex min-w-0 flex-col">
        <TypographySmall className="font-semibold text-foreground">{label}</TypographySmall>
        {href ? (
          <a
            href={href}
            className="truncate text-small text-muted-foreground transition-brand hover:text-primary"
          >
            {value}
          </a>
        ) : (
          <span className="truncate text-small text-muted-foreground">{value}</span>
        )}
      </span>
    </li>
  );
}
